import { h as __h } from "preact";
import { Component, h } from "preact";

/** @import { ssoProviderResponse as SsoProvider } from "~/backend/validate.js"; */
import { ssoProviderResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, response } from "~/lib/api.js";
import html from "~/util/html.js";
import { modal } from "~/util/modal.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import { object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import adminStyle from "~/admin/lib/style.js";
import input from "~/style/input.js";

/** @typedef {(name: string) => Promise<void>} SubmitCallback */

/**
 * @param {{ enterpriseId: string }} props
 * @param {SsoProvider} provider
 */
export const renameSsoProvider = async ({
  enterpriseId
}, provider) => {
  /** @type {SubmitCallback} */
  const onSubmit = async name => {
    await authedApiRequest("PATCH", `/v2/enterprises/${enterpriseId}/sso-providers/${provider.id}`, {
      json: {
        name
      },
      schema: response(ssoProviderResponse)
    });
    Query.invalidate([`/v2/enterprises/${enterpriseId}/sso-providers`]);
  };
  return await modal({
    title: "Rename SSO provider",
    content: (/** @type {() => void} */resolve) => h(ModalChildren, {
      resolve,
      provider,
      onSubmit
    })
  });
};

/**
 * @extends {Component<ModalChildrenProps>}
 *
 * @typedef {object} ModalChildrenProps
 * @property {SsoProvider} provider
 * @property {() => void} resolve
 * @property {SubmitCallback} onSubmit
 */
class ModalChildren extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    const form = new FormData(ev.currentTarget);
    const {
      name
    } = parse(object({
      name: string
    }), Object.fromEntries(form.entries()));
    await this.props.onSubmit(name);
    this.props.resolve();
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);
    return __h("form", {
      "class": adminStyle.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h(Field, {
      "for": "name",
      label: "Name",
      validity: {
        valueMissing: "Please enter a name for the provider."
      },
      error: error?.fields.name?.message
    }, __h("input", {
      "class": input.input,
      id: "name",
      name: "name",
      required: true,
      defaultValue: this.props.provider.name,
      autofocus: true
    })), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Rename"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, "Cancel")));
  }
}